import { ROLE_DEFAULTS } from "@tcg/api-client";

import { type AuthApi, AuthError, type Session } from "./types";

const notAvailable = () => Promise.reject(new AuthError("not_available"));

/** Used when no identity-service is configured (tests, misconfigured builds): nothing can sign in. */
export const notConnectedAuthApi: AuthApi = {
  signIn: notAvailable,
  verifyMfa: notAvailable,
  startEnrollment: notAvailable,
  confirmEnrollment: notAvailable,
  changePassword: notAvailable,
  forgotPassword: () => Promise.resolve(),
  resetPassword: notAvailable,
  checkInvite: notAvailable,
  acceptInvite: notAvailable,
  refresh: () => Promise.resolve(null),
  signOut: () => Promise.resolve(),
  listSessions: () => Promise.resolve([]),
  revokeOtherSessions: () => Promise.resolve(0),
};

function devSession(email: string): Session {
  return {
    accessToken: `dev.${btoa(email)}.${Date.now().toString(36)}`,
    expiresAt: Date.now() + 8 * 60 * 60 * 1000,
    sessionId: "dev-session",
    user: {
      id: "dev-owner",
      displayName: email.split("@")[0] ?? email,
      email,
      roles: ["owner"],
      permissions: [...ROLE_DEFAULTS.owner],
      totpEnabled: false,
    },
  };
}

/**
 * Development bypass (only when `VITE_AUTH_DEV_BYPASS=true` in a dev build): any non-empty email
 * and password yield an owner session valid for 8 hours so screens behind the guard can be built.
 * The owner carries every catalogued permission — the same set identity-service grants (no "*").
 */
export const devBypassAuthApi: AuthApi = {
  persistSession: true,
  signIn: ({ email, password }) => {
    if (!email.trim() || !password) {
      return Promise.reject(new AuthError("invalid"));
    }
    return Promise.resolve({ kind: "session", session: devSession(email) });
  },
  verifyMfa: notAvailable,
  startEnrollment: notAvailable,
  confirmEnrollment: notAvailable,
  changePassword: () => Promise.resolve(null),
  forgotPassword: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  checkInvite: notAvailable,
  acceptInvite: notAvailable,
  refresh: () => Promise.resolve(null),
  signOut: () => Promise.resolve(),
  listSessions: (session) =>
    Promise.resolve([
      {
        id: session.sessionId ?? "dev-session",
        userAgent: typeof navigator === "undefined" ? null : navigator.userAgent,
        ip: "127.0.0.1",
        createdAt: new Date(session.expiresAt - 8 * 60 * 60 * 1000).toISOString(),
        lastUsedAt: new Date().toISOString(),
        current: true,
      },
    ]),
  revokeOtherSessions: () => Promise.resolve(0),
};
